import {
    useEffect,
    useState,
    useMemo,
    type FC,
    useId
} from "react";
import {
    View
} from "react-native";
import type ICollapsibleProps from "./type";
import type {
    CollapsibleContextType
} from "./type";
import stylesheet from "./stylesheet";
import CollapsibleContent from "./components/content";
import CollapsibleTrigger from "./components/trigger";
import {
    CollapsibleContext
} from "./context";
import {
    getCollapsiblePlacement
} from "./util";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    warnOnce
} from "../../utils/development";

const Collapsible: FC<ICollapsibleProps> = ({
    isInitiallyOpen = false,
    isDisabled = false,
    isOpen: isOpenProp,
    customLocalize,
    onOpenChange,
    customTheme,
    children,
    style,
    ...props
}) => {
    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const [
        isOpenState,
        setIsOpenState
    ] = useState(isInitiallyOpen);

    const isControlled = isOpenProp !== undefined;

    const isOpen = isControlled ? isOpenProp : isOpenState;

    const placement = getCollapsiblePlacement({
        children
    });

    useEffect(() => {
        if(!isControlled || onOpenChange) {
            return;
        }

        warnOnce({
            message: "Collapsible: a controlled \"isOpen\" needs an \"onOpenChange\" handler that updates it."
        });
    }, [
        isControlled,
        onOpenChange
    ]);

    const handleToggle = useStableCallback(() => {
        if(isDisabled) {
            return;
        }

        const nextIsOpen = !isOpen;

        if(!isControlled) {
            setIsOpenState(nextIsOpen);
        }

        onOpenChange?.(nextIsOpen);
    });

    const context = useMemo<CollapsibleContextType>(() => ({
        contentID: `collapsible${baseID}`,
        customLocalize,
        handleToggle,
        customTheme,
        isDisabled,
        placement,
        isOpen
    }), [
        customLocalize,
        handleToggle,
        customTheme,
        isDisabled,
        placement,
        baseID,
        isOpen
    ]);

    return <CollapsibleContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container
            ]}
        >
            {children}
        </View>
    </CollapsibleContext.Provider>;
};
export default Object.assign(Collapsible, {
    Content: CollapsibleContent,
    Trigger: CollapsibleTrigger
});
